import React, { useContext, useState, useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Canvas, extend, useThree, useFrame } from 'react-three-fiber';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { Html } from '@react-three/drei';
import { Row, Col } from 'react-bootstrap';
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';

import SmallTabButton from '../../../components/common/forms/Button/SmallTabButton';
import BorderlessButton from '../../../components/common/forms/Button/BorderlessButton';
import { Colors } from '~/src/scss/common/style';
import xIcon from '../../../static/img/xCircle.svg';

interface PropsCanvasBox {
  data: Data;
  isSmall?: boolean;
  onClickFunc?: () => void;
}

interface PropsSingleProduct {
  product: any;
  isSmall?: boolean;
  isVisible?: boolean;
}

extend({ OrbitControls });

const CanvasContext = React.createContext({});

const Floor = () => {
  const pallet = useContext(CanvasContext);
  return (
    <mesh position={[0, -pallet.height, 0]} rotation-x={-Math.PI / 2}>
      <planeGeometry attach="geometry" args={[500, 500, 500]} />
      <meshBasicMaterial attach="material" color="black" />
      <gridHelper attach="helper" args={[500, 10, 0x111111, 0x111111]} rotation-x={-Math.PI / 2} />
    </mesh>
  );
};

const Pallet = () => {
  const pallet = useContext(CanvasContext);
  return (
    <mesh
      position={[
        0, // threejs-X coordinate | Normal X
        -pallet.height / 2, // threejs-Y coordinate | Normal Z
        0, // threejs-Z coordinate | Normal Y
      ]}
    >
      <boxBufferGeometry attach="geometry" args={[pallet.length, pallet.height, pallet.width]} />
      <meshBasicMaterial attach="material" color="yellow" />
    </mesh>
  );
};

const Cube = () => {
  const pallet = useContext(CanvasContext);
  return (
    <mesh position={[0, pallet.usableHeight / 2, 0]}>
      <boxBufferGeometry attach="geometry" args={[pallet.length + 2, pallet.usableHeight, pallet.width + 2]} />
      <meshBasicMaterial attach="material" color="orange" opacity={0.3} transparent />
    </mesh>
  );
};

const SingleProduct: React.FC<PropsSingleProduct> = ({ product = {}, isSmall = false, isVisible = false }) => {
  const [hovered, setHover] = useState(false);
  const pallet = useContext(CanvasContext);
  const checkRotation = useMemo(
    () => (isRotated) => {
      return isRotated ? Math.PI / 2 : 0;
    },
    [],
  );

  const handleHover = (e) => {
    e.stopPropagation();
    setHover(!hovered);
  };
  const x = product.zRotation
    ? product.x - pallet.length / 2 + product.width / 2
    : product.x - pallet.length / 2 + product.length / 2;

  const y = product.zRotation
    ? product.y - pallet.width / 2 + product.length / 2
    : product.y - pallet.width / 2 + product.width / 2;

  return isVisible ? (
    <>
      <mesh
        position={[x, product.z + product.height / 2, y]}
        rotation={[
          checkRotation(product.xRotation),
          checkRotation(product.zRotation),
          checkRotation(product.yRotation),
        ]}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...(!isSmall ? { onPointerOver: handleHover, onPointerOut: handleHover } : {})}
      >
        <boxBufferGeometry
          attach="geometry"
          args={[product.length - 0.5, product.height - 0.5, product.width - 0.5]}
        />
        <meshPhysicalMaterial
          attach="material"
          color={hovered ? 'red' : product.color || 'white'}
          roughness={0.5}
        />
        <Html
          scaleFactor={400}
          style={{
            pointerEvents: 'none',
            display: hovered ? 'block' : 'none',
          }}
        >
          <StyledCanvasContent className="canvas-content">
            <div>{`Product: ${product.info?.name}`}</div>
            <div>{`Quantity: ${product.info?.quantityDu}`}</div>
            <div>{`X: ${product.x} Y: ${product.y} Z: ${product.z} Height: ${product.height}`}</div>
          </StyledCanvasContent>
        </Html>
      </mesh>
    </>
  ) : null;
};

const Products = () => {
  const data = useContext(CanvasContext);

  return (
    <group>
      {data?.products?.map((product) => (
        <SingleProduct
          key={uuid()}
          product={product}
          isVisible={!data.currentLayer || data.currentLayer >= product.layer}
          isSmall={data.isSmall}
        />
      ))}
    </group>
  );
};

const Container = () => {
  return (
    <group>
      <Floor />
      <Cube />
      <Pallet />
      <Products />
    </group>
  );
};

const Scene = () => {
  return (
    <>
      <Container />
      <pointLight position={[500, 500, -500]} color="white" />
      <pointLight position={[-500, 500, 500]} color="white" />
    </>
  );
};

const CameraControls = () => {
  // Get a reference to the Three.js Camera, and the canvas html element.
  // We need these to setup the OrbitControls component.
  // https://threejs.org/docs/#examples/en/controls/OrbitControls

  const {
    camera,
    gl: { domElement },
  } = useThree();
  // Ref to the controls, so that we can update them on every frame using useFrame
  const controls = useRef();
  useFrame((state) => controls.current.update());
  return (
    <>
      <orbitControls
        ref={controls}
        args={[camera, domElement]}
        target={[0, 100, 0]}
        maxPolarAngle={Math.PI / 2}
        minDistance={300}
        maxDistance={300}
      />
    </>
  );
};

export default function CanvasBox({ data, isSmall = false, onClickFunc }: PropsCanvasBox): JSX.Element {
  const [currentLayer, setCurrentLayer] = useState(4);
  const layersFromData = useMemo(
    () =>
      data.products
        .flatMap((product, index) => (product.layer !== data.products[index + 1]?.layer ? product.layer : []))
        .reverse(),
    [data],
  );

  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === 'ArrowDown' && (currentLayer > 1 || currentLayer === null)) {
        setCurrentLayer(currentLayer === null ? layersFromData.length - 1 : currentLayer - 1);
      } else if (event.key === 'ArrowUp' && currentLayer !== null && currentLayer < layersFromData.length) {
        setCurrentLayer(currentLayer + 1);
      }
    }

    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentLayer]);

  return isSmall ? (
    <BoxWrapper>
      <div style={{ width: 128, height: 128 }}>
        <Canvas camera={{ position: [0, 200, 200] }}>
          <CameraControls />
          <color attach="background" args={['#333']} />
          <CanvasContext.Provider value={{ ...data, currentLayer, isSmall }}>
            <Scene />
          </CanvasContext.Provider>
        </Canvas>
      </div>
      <DescriptionWrapper>
        {`${data.name}, ${data.usableHeight} cm`}
        <br />
        {`(${data.width} x ${data.length})`}
      </DescriptionWrapper>
    </BoxWrapper>
  ) : (
    <WrapperBox>
      <CloseButton>
        <BorderlessButton onClick={onClickFunc} icon={xIcon} iconSize={38} />
      </CloseButton>
      <WrapperRowSizes>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            borderRight: '1px solid #a7a7a7',
            paddingRight: 30,
            marginRight: 30,
          }}
        >
          <SizeLabel>Height:</SizeLabel>
          <TextSize>{data.usableHeight}cm</TextSize>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <SizeLabel>To Top: </SizeLabel>
          <TextSize>{data.availableHeightToTop}cm</TextSize>
        </div>
      </WrapperRowSizes>
      <Row>
        <Col style={{ flexGrow: 0, alignSelf: 'flex-start' }}>
          <ButtonTitle>Leyers</ButtonTitle>
          <WrapperActions>
            {layersFromData.map((layerNumber) => (
              <SmallTabButton
                active={currentLayer === layerNumber}
                size="sm"
                onClick={() => {
                  setCurrentLayer(layerNumber);
                }}
                title={`${layerNumber}`}
              />
            ))}
          </WrapperActions>
        </Col>
        <Col>
          <WrapperCanvas>
            <Canvas camera={{ position: [0, 130, 15] }}>
              <CameraControls />
              <color attach="background" args={['#333']} />
              <CanvasContext.Provider value={{ ...data, currentLayer, isSmall }}>
                <Scene />
              </CanvasContext.Provider>
            </Canvas>
          </WrapperCanvas>
        </Col>
      </Row>
    </WrapperBox>
  );
}

const WrapperCanvas = styled.div`
  min-height: 70vh;
  max-height: 680px;
  max-width: 541px;
  aspect-ratio: 3/2;

  div {
    border-radius: 10px;
  }
`;

const WrapperActions = styled.div`
  border: 1px solid #eeeeee;
  border-radius: 5px;
  overflow: hidden;
  buton:first-child {
    border-radius: 15px;
  }
`;

const CloseButton = styled.div`
  position: absolute;
  top: 15px;
  right: 15px;
`;

const WrapperRowSizes = styled(Row)`
  margin-left: 100px;
  margin-bottom: 25px;
  display: flex;
`;

const WrapperBox = styled.div`
  positon: relative;
  &:after {
    background: #fff;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }
`;

const StyledCanvasContent = styled.div`
  width: 200px;
  padding: 10px;
  transform: translate3d(10%, 0, 0);
  text-align: right;
  color: #fff;
  font-size: 14px;
  background: rgba(0, 0, 0, 0.8);

  &:after {
    position: absolute;
    top: 0;
    left: 0;
    content: '';
    width: 200px;
    height: 1px;
    background: #fff;
  }
`;

const DescriptionWrapper = styled.div`
  color: #642b26;
  font-size: 14px;
  text-align: center;
`;

const BoxWrapper = styled.div`
  position: realtive;
  width: 128px;
  background: #fff;
  border-radius: 5px;
  overflow: hidden;
`;

const ButtonTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${Colors.darkBrown}!important;
`;

const TextSize = styled.p`
  color: #343434;
  font-size: 18px;
  font-weight: 700;
  margin: 0;
`;

const SizeLabel = styled.p`
  color: #a7a7a7;
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 5px;
`;
